import { Link } from "react-router-dom";
import {
  Users, Vote, MessagesSquare, CalendarDays, ShieldCheck, Share2, Lightbulb,
  UserPlus, ArrowRight, Check, Sparkles, Bell,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

/* GroupTrip Planner — product page for the group-travel pillar (Feb-2026 unbundling).
   All capabilities below are live features of shared trips in My Travel Hub. */

const FEATURES = [
  { icon: CalendarDays, color: "text-violet-700", title: "One shared itinerary",
    text: "Everyone sees the same day-by-day plan — drag & drop activities, live co-editing, and an AI planner to draft it in seconds." },
  { icon: Vote, color: "text-blue-700", title: "Polls & voting",
    text: "Beach day or backwaters? Create a poll on the trip, let the group vote, and settle it without a 200-message chat thread." },
  { icon: MessagesSquare, color: "text-emerald-700", title: "Group chat on the trip",
    text: "A private thread right beside the plan — every traveler on the trip can chat, even view-only members." },
  { icon: Lightbulb, color: "text-amber-600", title: "Friend suggestions",
    text: "Share a read-only link and friends can suggest activities — you accept them into the plan with one tap." },
  { icon: Bell, color: "text-rose-600", title: "Evening trip briefs",
    text: "The night before each travel day, everyone gets tomorrow's plan with the weather — by email, push and in-app." },
  { icon: Share2, color: "text-cyan-700", title: "WhatsApp-ready sharing",
    text: "Send the plan to the family group in one tap — a clean read-only page, no account needed to view." },
];

const ROLES = [
  ["Organizer", "Owns the trip — edits everything, manages members, controls expenses and documents.", "bg-violet-100 text-violet-800 border-violet-300"],
  ["Editor", "Co-plans the itinerary, adds activities, chats and votes — no access to documents or money.", "bg-blue-100 text-blue-800 border-blue-300"],
  ["Financial manager", "A trusted person you grant the expense view — logs costs and sees the settlement.", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  ["Viewer", "Sees the plan, chats and votes. Nothing else — passports and expenses stay invisible.", "bg-zinc-100 text-zinc-700 border-zinc-300"],
];

export default function GroupTripPlanner() {
  const { user } = useAuth();
  return (
    <div data-testid="grouptrip-page">
      <section className="relative border-b border-zinc-200 bg-violet-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-violet-300 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> GroupTrip Planner — by TravelerPulse</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="grouptrip-title">
            Plan together.<br />Argue less. Travel more.
          </h1>
          <p className="text-white/85 text-lg mt-4 max-w-2xl">
            One shared itinerary for the whole group — with polls, voting, chat and clear roles,
            so everyone helps plan and nobody sees what they shouldn't.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to={user ? "/travel-hub#hub-trips" : "/register"} data-testid="grouptrip-cta"
              className="inline-flex items-center gap-2 bg-violet-500 text-white px-7 py-3.5 font-700 hover:bg-violet-400 transition-colors">
              <UserPlus className="w-5 h-5" /> {user ? "Start a group trip" : "Start free — 45 days, no card"}
            </Link>
            <Link to="/plan-ai" data-testid="grouptrip-ai-link"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-7 py-3.5 font-700 hover:bg-white/20 transition-colors">
              <Sparkles className="w-5 h-5" /> Draft it with AI first
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="grouptrip-features">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-8">Everything a group trip needs</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-zinc-300 bg-white p-5">
              <f.icon className={`w-7 h-7 ${f.color}`} />
              <div className="font-heading font-900 text-lg mt-3">{f.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="grouptrip-roles">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-rose-700 mb-2 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Item-level permissions</div>
          <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-2">Grandma votes on dinner. She doesn't see your passport.</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">Each person gets a role — and every item (itinerary, chat, expenses, documents) has its own access rule.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ROLES.map(([name, text, cls]) => (
              <div key={name} className="border border-zinc-300 bg-white p-5">
                <span className={`inline-block px-2.5 py-1 text-xs font-800 uppercase tracking-wide border ${cls}`}>{name}</span>
                <p className="text-sm text-zinc-700 mt-3">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        <div className="border border-zinc-300 bg-white p-8 sm:p-10">
          <h2 className="font-heading font-900 text-2xl mb-5">How a group trip comes together</h2>
          <ol className="space-y-3 text-sm text-zinc-700">
            {[
              "Create the trip (or let the AI planner draft the itinerary from one sentence).",
              "Invite the group — pick each person's role; they get an email with a join link.",
              "Post a poll for the big decisions and let everyone vote.",
              "Co-edit the day plan; friends with the view link can suggest activities you approve.",
              "On the road: evening briefs, live flight alerts and the offline trip kit keep everyone in sync.",
            ].map((s, i) => (
              <li key={i} className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-violet-700 text-white text-xs font-800 flex items-center justify-center">{i + 1}</span>{s}</li>
            ))}
          </ol>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={user ? "/travel-hub#hub-trips" : "/register"} data-testid="grouptrip-bottom-cta"
              className="inline-flex items-center gap-2 bg-violet-700 text-white px-6 py-3 font-700 hover:bg-violet-600 transition-colors">
              {user ? "Open my trips" : "Create your free account"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/travel-hub" className="inline-flex items-center gap-2 border border-zinc-300 px-6 py-3 font-700 text-zinc-700 hover:border-primary hover:text-primary transition-colors">
              Explore My Travel Hub
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Included in every TravelerPulse membership — and in the 45-day free trial.</p>
        </div>
      </section>
    </div>
  );
}
